import { MulticityFlights } from "@b2bportal/air-shopping-api";
import { AnyAction } from "redux";
import { CoreFlightsCheckout, ITripTerminus, TripCategory } from "redmond";

import {
  setAllMulticityRoutes,
  setTripCategory,
} from "../../search/actions/actions";
import { MulticityRoute } from "../../search/reducer";
import { setFlightShop } from "../../shop/actions/actions";
import { ISelectedMulticityTrip } from "../../shop/reducer";

type ShopSetUpdate = {
  flights: CoreFlightsCheckout["flights"];
  tripDetailsById: CoreFlightsCheckout["tripDetailsById"];
  selectedTrip: CoreFlightsCheckout["selectedTrip"];
  prediction: CoreFlightsCheckout["prediction"];
  selectedMulticityTrips?: ISelectedMulticityTrip[];
  multicityFlights?: MulticityFlights;
};

type CoreMulticitySearchLeg = {
  origin: ITripTerminus;
  destination: ITripTerminus;
  departureDate: Date;
};

// The native (b2bportal) shop hands checkout off to this legacy microfrontend via the
// `core` prop, which carries extra multicity fields — map them into the shapes the
// legacy book flow expects.
export const mapCoreFlightsCheckoutToShopUpdate = (
  core: CoreFlightsCheckout,
): {
  tripCategory: TripCategory;
  shopSet: ShopSetUpdate;
  multicityRoutes?: MulticityRoute[];
} => {
  const isMulticityCore = core.tripCategory === TripCategory.MULTI_CITY;

  const getTripCategory = (): TripCategory => {
    // Checked first so `selectedTrip.returnSliceId` is never read for multicity,
    // where `selectedTrip` may still be the empty initial value.
    if (isMulticityCore) return TripCategory.MULTI_CITY;
    return core.selectedTrip.returnSliceId != null
      ? TripCategory.ROUND_TRIP
      : TripCategory.ONE_WAY;
  };
  const tripCategory = getTripCategory();

  const shopSet: ShopSetUpdate = {
    flights: core.flights,
    tripDetailsById: core.tripDetailsById,
    selectedTrip: core.selectedTrip,
    prediction: core.prediction,
    ...(isMulticityCore
      ? {
          selectedMulticityTrips: core.multicitySelectedLegs.map(
            (leg: {
              sliceId: string;
              fareId: string;
              fareRating?: number;
            }): ISelectedMulticityTrip => ({
              tripId: core.selectedTrip.tripId,
              departureSliceId: leg.sliceId,
              departureFareId: leg.fareId,
              departureFareRating: leg.fareRating,
            }),
          ),
          // Must be the whole summary: legacy reads `.departures` off this, and its
          // `isEmpty` guard passes for a partial object.
          multicityFlights: core.multicityFlights,
        }
      : {}),
  };

  // Breadcrumb labels come from legacy search state, which only the legacy shop page
  // populates — without these they collapse to a bare "to".
  const multicityRoutes: MulticityRoute[] | undefined = isMulticityCore
    ? core.multicitySearchedLegs?.map(
        (leg: CoreMulticitySearchLeg): MulticityRoute => ({
          origin: leg.origin,
          destination: leg.destination,
          departureDate: leg.departureDate,
        }),
      )
    : undefined;

  return { tripCategory, shopSet, multicityRoutes };
};

// Seeds the legacy store from the `core` handoff. Extracted from the mounting effect
// so the dispatch sequence itself is testable.
export const buildCoreFlightsCheckoutActions = (
  core: CoreFlightsCheckout,
): AnyAction[] => {
  const { tripCategory, shopSet, multicityRoutes } =
    mapCoreFlightsCheckoutToShopUpdate(core);

  return [
    setTripCategory(tripCategory),
    setFlightShop(shopSet),
    // Multicity breadcrumbs read this slice; single-trip must not touch it.
    ...(multicityRoutes != null ? [setAllMulticityRoutes(multicityRoutes)] : []),
  ];
};
